<template>
  <v-form ref="form" v-model="formValid">
    <v-row no-gutters>
      <v-col cols="12" md="6" class="pr-md-6">
        <v-autocomplete
          outlined
          v-model="selectedSchemaName"
          :items="schemaOptions"
          :loading="loadingSchemas"
          label="Schema Reference"
          hide-details="auto"
        ></v-autocomplete>

        <template v-if="selectedSchema">
          <div v-if="scalarFields.length" class="mt-6">
            <Typography variant="caption-2" class="text-uppercase mb-2 d-flex justify-start">
              Scalar Fields
            </Typography>
            <div v-for="(field, index) in scalarFields" :key="field.name">
              <div class="d-flex align-center justify-space-between py-2">
                <Typography variant="body-2">{{ field.name }}</Typography>
                <code class="scalar-type">{{ field.type }}</code>
              </div>
              <v-divider v-if="index < scalarFields.length - 1" />
            </div>
          </div>

          <div v-if="relationshipFields.length" class="mt-6">
            <Typography variant="caption-2" class="text-uppercase mb-2 d-flex justify-start">
              Relationships
            </Typography>
            <div v-for="(field, index) in relationshipFields" :key="field.name">
              <div class="d-flex align-center justify-space-between py-2">
                <Typography variant="body-2">{{ field.name }}</Typography>
                <code class="relationship-type">{{ field.type }}</code>
              </div>
              <v-divider v-if="index < relationshipFields.length - 1" />
            </div>
          </div>
        </template>
      </v-col>
      <v-divider vertical class="d-none d-md-flex" />
      <v-col cols="12" md="6" class="pl-md-6">
        <v-text-field
          outlined
          v-model="groupName"
          :rules="[
            (v) => !!v || 'Group name is required',
            (v) => !!(v && v.length < 255) || 'Group name should be shorter',
          ]"
          label="Group Name*"
          class="mb-4"
        ></v-text-field>
        <v-textarea
          outlined
          v-model="groupRuleQuery"
          :rules="[(v) => !!v || 'Group rule query is required']"
          label="Group Rule Query*"
          rows="10"
          class="mb-4"
        ></v-textarea>
        <v-text-field
          outlined
          v-model="userIdPath"
          :rules="[(v) => !!v || 'UserId path is required']"
          label="UserId Path*"
        ></v-text-field>
        <v-card-actions class="px-0 mt-4">
          <v-spacer></v-spacer>
          <Button text @click="$emit('cancel')">Cancel</Button>
          <Button @click="handleSubmit" :disabled="!formValid">{{ submitLabel }}</Button>
        </v-card-actions>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import Button from "@/components/generic/Button.vue";
import Typography from "@/components/generic/Typography.vue";
import InformService from "@/services/InformService";

export default {
  name: "InformGroupEditor",
  components: {
    Button,
    Typography,
  },
  props: {
    clientId: {
      type: [Number, String],
      default: null,
    },
    initialGroupName: {
      type: String,
      default: "",
    },
    initialGroupRuleQuery: {
      type: String,
      default: "",
    },
    initialUserIdPath: {
      type: String,
      default: "",
    },
    submitLabel: {
      type: String,
      default: "Save",
    },
  },
  data() {
    return {
      informService: new InformService(),
      formValid: false,
      selectedSchemaName: null,
      groupName: this.initialGroupName,
      groupRuleQuery: this.initialGroupRuleQuery,
      userIdPath: this.initialUserIdPath,
      schemas: [],
      loadingSchemas: false,
    };
  },
  computed: {
    schemaOptions() {
      return this.schemas.map((schema) => schema.name);
    },
    selectedSchema() {
      if (!this.selectedSchemaName) return null;
      return this.schemas.find((schema) => schema.name === this.selectedSchemaName) || null;
    },
    scalarFields() {
      return (this.selectedSchema?.fields || []).filter((field) => field.isScalar);
    },
    relationshipFields() {
      return (this.selectedSchema?.fields || []).filter((field) => field.isRelationship);
    },
  },
  created() {
    this.loadSchemas();
  },
  methods: {
    async loadSchemas() {
      if (!this.clientId) return;
      try {
        this.loadingSchemas = true;
        const response = await this.informService.getSchema({ clientId: this.clientId });
        this.schemas = response?.result || [];
      } catch (error) {
        console.error("Failed to load Inform schemas", error);
        this.schemas = [];
      } finally {
        this.loadingSchemas = false;
      }
    },
    handleSubmit() {
      if (!this.formValid) return;
      this.$emit("submit", {
        groupName: this.groupName,
        groupRuleQuery: this.groupRuleQuery,
        userIdPath: this.userIdPath,
      });
    },
  },
  watch: {
    clientId() {
      this.loadSchemas();
    },
  },
};
</script>

<style scoped>
.scalar-type {
  background-color: var(--v-infoBackground-base) !important;
  color: var(--v-infoText-base) !important;
}
.relationship-type {
  background-color: var(--v-successBackground-base) !important;
  color: var(--v-successText-base) !important;
}
</style>
